import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useAuth } from '../../../AuthContext';
import { setPipelineRefresh } from '../../../redux/pipelineRefresh';
import { useAppSelector, useAppDispatch } from '../../../redux/store';

interface CriteriaValue {
  id: string;
  name: string;
}

interface Criteria {
  criteriaName: string;
  criteriaValue: CriteriaValue[];
}

interface Checkpoint {
  checkpointDescription: string;
  checkpointName: string;
  checkpointOrder: number;
  criteria: Criteria[];
  status?: 'completed' | 'partial' | 'failed' | 'empty';
}

interface Pipeline {
  id: string;
  pipelineName: string;
  pipelineDescription: string;
  checkpoints: Checkpoint[];
  isActive?: boolean;
  category?: string;
}

interface MultiplePipelineProps {
  leadId?: string;
  maxVisiblePipelines?: number;
  showCategories?: boolean;
  allowCollapse?: boolean;
  // function to pass count of pipelines to parent component
  onPipelineCountChange?: (count: number) => void;
}

export default function MultiplePipelineComponent({
  leadId,
  maxVisiblePipelines = 3,
  showCategories = true,
  allowCollapse = true,
  onPipelineCountChange = () => { /* Default no-op function */ }
}: MultiplePipelineProps) {
  const { userData, currentUser } = useAuth();
  const [leadData, setLeadData] = useState<any>(null);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(false);
  const [collapsedPipelines, setCollapsedPipelines] = useState<Set<string>>(new Set());
  const [showAllPipelines, setShowAllPipelines] = useState(false);

  const dispatch = useAppDispatch();
  const pipelineRefresh = useAppSelector(state => state.pipelineRefresh.refresh);

  const criteriaOptions = [
    // Application Process
    { label: "Welcome Call Status", value: "welcomeCallByAdmissionTeamStatusId", key: "welcomeCallByAdmissionTeamStatus" },
    { label: "Visa Processing Status", value: "visaFileProcessingStatusId", key: "visaFileProcessingStatus" },
    { label: "Application Status", value: "applicationMovedStatusId", key: "applicationMovedStatus" },
    { label: "Offer Letter Status", value: ["conditionalOfferLetterStatusId", "unconditionalOfferLetterStatusId"], key: "offerLetterStatus" },
    // Payments and Documents
    { label: "Fee Payment Status", value: "feePaymentStatusId", key: "feePaymentStatus" },
    { label: "File Submission Status", value: "fileSubmissionStatusId", key: "fileSubmissionStatus" },
    // Lead Management
    { label: "Lead Source", value: "sourceId", key: "source" },
    { label: "Lead Stage", value: "stageId", key: "stage" },
    { label: "Lead State", value: "stateId", key: "state" },
    { label: "Lead Status", value: "statusId", key: "status" },
    { label: "Admission Status", value: "admissionStatusId", key: "admissionStatus" },
    // Admission Process
    { label: "Preferred Streams", value: "preferredStreamId", key: "preferredStream" },
    { label: "Program Types", value: "programTypeId", key: "programType" },
  ];

  // Function to determine checkpoint status (same as original)
  const determineCheckpointStatus = (checkpoint: Checkpoint, leadData: any) => {
    if (!leadData) return 'empty';

    let hasAnyMatch = false;
    let hasAnyNull = false;
    let allMatch = true;
    let allNull = true;
    let anyFailed = false;

    for (const criteria of checkpoint.criteria) {
      const criteriaField = criteriaOptions.find(option => option.label === criteria.criteriaName)?.value;
      if (!criteriaField) continue;

      let leadValues: any[] = [];

      if (Array.isArray(criteriaField)) {
        for (const field of criteriaField) {
          if (leadData[field]) {
            leadValues.push(leadData[field]);
          }
        }
      } else if (typeof criteriaField === 'string') {
        if (leadData[criteriaField]) {
          leadValues.push(leadData[criteriaField]);
        }
      }

      leadValues = leadValues.flat();

      if (leadValues.length === 0) {
        hasAnyNull = true;
        allMatch = false;
        allNull = allNull && true;
      } else {
        allNull = false;
        const criteriaMatch = criteria.criteriaValue.some(val => {
          return leadValues.some((leadValue: any) => {
            if (typeof leadValue === 'string') {
              return leadValue.toLowerCase() === val.name.toLowerCase() ||
                leadValue.toLowerCase() === val.id.toLowerCase();
            }
            if (Array.isArray(leadValue)) {
              return leadValue.some((v: string) =>
                v.toLowerCase() === val.name.toLowerCase() ||
                v.toLowerCase() === val.id.toLowerCase()
              );
            }
            return false;
          });
        });

        if (criteriaMatch) {
          hasAnyMatch = true;
        } else {
          anyFailed = true;
          allMatch = false;
        }
      }
    }

    if (allNull) return 'empty';
    if (anyFailed) return 'failed';
    if (allMatch) return 'completed';
    if (hasAnyMatch && hasAnyNull) return 'partial';
    return 'failed';
  };

  // Calculate pipeline progress
  // Calculate percentage for display (simple division)
  const calculatePipelinePercentage = (checkpoints: Checkpoint[]) => {
    if (checkpoints.length === 0) return 0;

    let completedCount = 0;

    // Count consecutive completed checkpoints from the beginning
    for (let i = 0; i < checkpoints.length; i++) {
      if (checkpoints[i].status === 'completed') {
        completedCount++;
      } else {
        // Stop at first non-completed checkpoint
        break;
      }
    }

    // Calculate progress as: (completed checkpoints / total checkpoints) * 100
    return (completedCount / checkpoints.length) * 100;
  };

  // Calculate progress line position (moves checkpoint to checkpoint)
  const calculatePipelineProgress = (checkpoints: Checkpoint[]) => {
    if (checkpoints.length === 0) return 0;

    let progressPercentage = 0;

    for (let i = 0; i < checkpoints.length; i++) {
      const checkpoint = checkpoints[i];

      if (checkpoint.status === 'completed') {
        // If this checkpoint is completed, progress moves to the next checkpoint position
        if (i === checkpoints.length - 1) {
          // If it's the last checkpoint, progress is 100%
          progressPercentage = 100;
        } else {
          // Progress moves to the next checkpoint position
          progressPercentage = ((i + 1) / (checkpoints.length - 1)) * 100;
        }
      } else {
        // If this checkpoint is not completed, progress stops here
        break;
      }
    }

    return progressPercentage;
  };

  // Get pipeline status summary
  const getPipelineSummary = (checkpoints: Checkpoint[]) => {
    const total = checkpoints.length;
    const completed = checkpoints.filter(cp => cp.status === 'completed').length;
    const partial = checkpoints.filter(cp => cp.status === 'partial').length;
    const failed = checkpoints.filter(cp => cp.status === 'failed').length;
    const empty = checkpoints.filter(cp => cp.status === 'empty').length;

    let overallStatus: 'completed' | 'partial' | 'failed' | 'empty' = 'empty';

    if (completed === total) {
      overallStatus = 'completed';
    } else if (completed > 0 || partial > 0) {
      overallStatus = 'partial';
    } else if (failed > 0) {
      overallStatus = 'failed';
    }

    const progress = calculatePipelineProgress(checkpoints);
    const percentage = calculatePipelinePercentage(checkpoints);

    return { total, completed, partial, failed, empty, overallStatus, progress, percentage };
  };

  // Fetch all pipelines
  useEffect(() => {
    const fetchPipelineData = async () => {
      if (!userData?.companyId) return;

      try {
        // geet user document to get pipeline data
        setLoading(true);
        const userDocRef = doc(db, `users/${currentUser?.uid}`);
        const userDoc = await getDoc(userDocRef);
        if (!userDoc.exists()) {
          console.error("User document does not exist");
          return;
        }
        const user = userDoc.data();
        if (!user || !user.companyId) {
          console.error("User data or company ID is missing");
          return;
        }
        // user pipelines
        const userPipelines = user.pipelineIds || [];

        const leadRef = doc(db, `companies/${user.companyId}/leads/${leadId}`);
        const leadSnapshot = await getDoc(leadRef);

        let leadPipelines = [];
        if (leadSnapshot.exists()) {
          const leadData = leadSnapshot.data();
          leadPipelines = leadData?.pipelines
        } else {
          console.log("No such lead found!");
        }

        const commonPipelines = leadPipelines.filter((pipeline: any) =>
          userPipelines.includes(pipeline)
        );

        // Fetch pipelines 
        const pipelineData: Pipeline[] = [];
        for (const pipelineId of commonPipelines) {
          const pipelineDocRef = doc(db, `companies/${user.companyId}/leadPipelines`, pipelineId);
          const pipelineDoc = await getDoc(pipelineDocRef);
          if (pipelineDoc.exists()) {
            const pipeline = pipelineDoc.data() as Pipeline;
            pipeline.id = pipelineDoc.id; // Add ID to the pipeline object
            pipelineData.push(pipeline);
          }
        }
        console.log("Fetched pipelines:", pipelineData);

        // Set pipelines state
        setPipelines(pipelineData);

        // Initialize with all pipelines collapsed
        const allPipelineIds = new Set(pipelineData.map(p => p.id));
        setCollapsedPipelines(allPipelineIds);
        setInitialLoad(true);
      } catch (error) {
        console.error("Error fetching pipeline data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPipelineData();
  }, [userData?.companyId, pipelineRefresh]);

  // Fetch lead data
  useEffect(() => {
    const fetchLeadData = async () => {
      if (!leadId || !userData?.companyId) return;

      try {
        const leadDocRef = doc(db, `companies/${userData.companyId}/leads`, leadId);
        const leadDoc = await getDoc(leadDocRef);

        if (leadDoc.exists()) {
          setLeadData(leadDoc.data());
        }
      } catch (error) {
        console.error("Error fetching lead data:", error);
      }
    };

    fetchLeadData();
  }, [leadId, userData?.companyId, pipelineRefresh]);

  // Update pipeline statuses when leadData changes
  useEffect(() => {
    if (pipelines.length === 0 || !leadData) return;

    const updatedPipelines = pipelines.map(pipeline => ({
      ...pipeline,
      checkpoints: pipeline.checkpoints.map(cp => ({
        ...cp,
        status: determineCheckpointStatus(cp, leadData) as 'completed' | 'partial' | 'failed' | 'empty'
      })).sort((a, b) => a.checkpointOrder - b.checkpointOrder)
    }));

    console.log("Updated pipelines with statuses:", updatedPipelines);


    setPipelines(updatedPipelines);
    dispatch(setPipelineRefresh(false));
    setInitialLoad(false);
  }, [initialLoad]);

  // Initialize collapsed state with ALL pipelines collapsed
  useEffect(() => {
    if (pipelines.length > 0) {
      setCollapsedPipelines(new Set(pipelines.map(p => p.id)));

      onPipelineCountChange(pipelines.length); // Notify parent component of pipeline count
    }
  }, [pipelines]);

  // Toggle function (updated)
  const togglePipelineCollapse = (pipelineId: string) => {
    setCollapsedPipelines(prev => {
      // If pipeline is NOT in collapsed set (meaning it's expanded), collapse it
      if (!prev.has(pipelineId)) {
        return new Set(pipelines.map(p => p.id)); // Collapse all
      } else {
        // If pipeline is collapsed, expand ONLY this one
        const newSet = new Set(pipelines.map(p => p.id));
        newSet.delete(pipelineId);
        return newSet;
      }
    });
  };

  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'completed':
        return {
          border: 'border-green-500',
          icon: <CheckCircle className="text-green-500" size={16} />,
          bg: 'bg-green-100',
          text: 'text-green-800',
          badgeBg: 'bg-green-500',
          hexValue: '#10B981'
        };
      case 'partial':
        return {
          border: 'border-yellow-500',
          icon: <AlertCircle className="text-yellow-500" size={16} />,
          bg: 'bg-yellow-100',
          text: 'text-yellow-800',
          badgeBg: 'bg-yellow-500',
          hexValue: '#F59E0B'
        };
      case 'failed':
        return {
          border: 'border-red-500',
          icon: <XCircle className="text-red-500" size={16} />,
          bg: 'bg-red-100',
          text: 'text-red-800',
          badgeBg: 'bg-red-500',
          hexValue: '#EF4444'
        };
      default:
        return {
          border: 'border-gray-300',
          icon: <XCircle className="text-gray-400" size={16} />,
          bg: 'bg-gray-100',
          text: 'text-gray-800',
          badgeBg: 'bg-gray-400',
          hexValue: '#9CA3AF'
        };
    }
  };

  if (loading) {
    return <div className="w-full flex justify-center py-10">Loading pipeline data...</div>;
  }

  if (!pipelines.length) {
    return <div className="w-full flex justify-center py-10">No pipeline data found</div>;
  }

  const visiblePipelines = showAllPipelines ? pipelines : pipelines.slice(0, maxVisiblePipelines);
  const hasMorePipelines = pipelines.length > maxVisiblePipelines;

  return (
    <div className="w-full space-y-6 ">
      {/* Header */}
      <div className="flex justify-end items-center">
        {/* <h2 className="text-2xl font-bold text-gray-800">Lead Pipelines</h2> */}
        {/* <div className="absolute top-[-18%] right-10 text-sm text-gray-600">
          {pipelines.length} pipeline{pipelines.length !== 1 ? 's' : ''} configured
        </div> */}
      </div>

      {/* Pipelines */}
      <div className="space-y-4 ">
        {visiblePipelines.map((pipeline) => {
          const summary = getPipelineSummary(pipeline.checkpoints);
          const isCollapsed = collapsedPipelines.has(pipeline.id);
          const statusStyles = getStatusStyles(summary.overallStatus);

          return (
            <div key={pipeline.id} onClick={() => togglePipelineCollapse(pipeline.id)} className={`bg-white rounded-lg shadow-sm border border-gray-200 cursor-pointer transition-all duration-300 hover:shadow-md px-3`}
              // border start color based on status
              style={{
                borderLeftColor: statusStyles.hexValue,
                borderLeftWidth: '4px',
                borderLeftStyle: 'solid'
              }}
            >
              {/* Pipeline Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${statusStyles.badgeBg}`}></div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">{pipeline.pipelineName}</h3>
                    </div>
                  </div>

                  <div className="flex mb-3 items-center space-x-4">
                    {/* Progress Summary */}
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-800">
                        {summary.completed}/{summary.total} Completed
                      </div>
                      
                      {/* <div className="text-xs text-gray-500">
                        {summary.percentage.toFixed(0)}% Progress
                      </div> */}
                    </div>

                    {/* Progress Bar */}
                    {/* <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 transition-all duration-300"
                        style={{ width: `${summary.progress}%` }}
                      ></div>
                    </div> */}

                    {/* Collapse Button */}
                    {allowCollapse && (
                      <button
                        onClick={() => togglePipelineCollapse(pipeline.id)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {isCollapsed ? (
                          <ChevronDown size={20} className="text-gray-500" />
                        ) : (
                          <ChevronUp size={20} className="text-gray-500" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Pipeline Timeline */}
              {(!allowCollapse || !isCollapsed) && (
                <div className="p-4">
                  <div className="relative w-full mb-9">
                    {/* Background Line */}
                    <div className="absolute top-3.5 left-0 right-0 h-1.5 bg-gray-200 rounded-full z-0"></div>

                    {/* Progress Line */}
                    {summary.progress > 0 && (
                      <div
                        className="absolute top-3.5 left-0 h-1.5 bg-green-500 rounded-full z-0 transition-all duration-500 ease-out"
                        style={{ width: `${summary.progress}%` }}
                      ></div>
                    )}

                    {/* Checkpoints */}
                    <div className="relative flex justify-between w-full z-10">
                      {pipeline.checkpoints.map((checkpoint, index) => {
                        const positionPercentage = pipeline.checkpoints.length > 1
                          ? (index / (pipeline.checkpoints.length - 1)) * 100
                          : 50;
                        const status = checkpoint.status || 'empty';
                        const isFirst = index === 0;
                        const isLast = index === pipeline.checkpoints.length - 1;
                        const checkpointStyles = getStatusStyles(status);

                        return (
                          <div
                            key={`${checkpoint.checkpointName}-${index}`}
                            className="absolute flex flex-col items-center group"
                            style={{
                              left: `${positionPercentage}%`,
                              transform: 'translateX(-50%)',
                            }}
                          >
                            {/* Checkpoint Indicator */}
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center bg-white border-4 ${checkpointStyles.border} `}
                            >
                              {checkpointStyles.icon}
                            </div>

                            {/* Checkpoint Name (Always visible for multiple pipelines) */}
                            {/* <div className={`mt-2 text-xs font-medium ${checkpointStyles.text} text-center max-w-20`}>
                              {checkpoint.checkpointName}
                            </div> */}

                            {/* Detailed Tooltip on Hover */}
                            <div className={`
                              absolute bottom-12 invisible group-hover:visible z-[999]
                              ${isFirst ? 'left-0 origin-left' : ''}
                              ${isLast ? 'right-0 origin-right' : ''}
                              ${!isFirst && !isLast ? 'left-1/2 transform -translate-x-1/2' : ''}
                            `}>
                              <div className={`
                                px-4 py-3 ${checkpointStyles.bg} ${checkpointStyles.text} text-sm rounded-md 
                                shadow-2xl w-96 transform transition-all duration-200 
                                scale-95 group-hover:scale-100 border ${checkpointStyles.border}
                                ${isFirst ? 'ml-2' : ''}
                                ${isLast ? 'mr-2' : ''}
                              `}>
                                <div className="font-semibold text-base mb-2">
                                  {checkpoint.checkpointName}
                                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${checkpointStyles.border}`}>
                                    {status === 'completed' && 'Completed'}
                                    {status === 'partial' && 'Partially Matched'}
                                    {status === 'failed' && 'Not Matched'}
                                    {status === 'empty' && 'No Data'}
                                  </span>
                                </div>

                                {checkpoint.checkpointDescription && (
                                  <p className="mb-3">{checkpoint.checkpointDescription}</p>
                                )}

                                {checkpoint.criteria && checkpoint.criteria.length > 0 && (
                                  <div className="space-y-3">
                                    {checkpoint.criteria.map((criteria, critIdx) => {
                                      const criteriaField = criteriaOptions.find(option => option.label === criteria.criteriaName)?.value;
                                      let leadValues: any[] = [];

                                      if (criteriaField) {
                                        if (Array.isArray(criteriaField)) {
                                          for (const field of criteriaField) {
                                            if (leadData?.[field]) {
                                              leadValues.push(leadData[field]);
                                            }
                                          }
                                        } else if (typeof criteriaField === 'string' && leadData?.[criteriaField]) {
                                          leadValues.push(leadData[criteriaField]);
                                        }
                                      }

                                      leadValues = leadValues.flat();
                                      const hasValue = leadValues.length > 0;

                                      return (
                                        <div key={critIdx} className="border-t border-gray-300 pt-2">
                                          <div className="font-medium">
                                            {criteria.criteriaName}
                                            {!hasValue && <span className="ml-2 text-xs text-gray-500">(No data)</span>}
                                          </div>
                                          <div className="flex flex-wrap gap-2 mt-2">
                                            {criteria.criteriaValue.map((value, valIdx) => {
                                              const isMatch = hasValue && leadValues.some((leadValue: any) => {
                                                if (typeof leadValue === 'string') {
                                                  return leadValue.toLowerCase() === value.name.toLowerCase() ||
                                                    leadValue.toLowerCase() === value.id.toLowerCase();
                                                }
                                                if (Array.isArray(leadValue)) {
                                                  return leadValue.some((v: string) =>
                                                    v.toLowerCase() === value.name.toLowerCase() ||
                                                    v.toLowerCase() === value.id.toLowerCase()
                                                  );
                                                }
                                                return false;
                                              });

                                              return (
                                                <span
                                                  key={valIdx}
                                                  className={`px-2 py-1 rounded text-xs ${isMatch ? 'bg-green-600 text-white' :
                                                    hasValue ? 'bg-gray-500 text-white' : 'bg-gray-300 text-gray-700'
                                                    }`}
                                                >
                                                  {value.name}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Show More/Less Button */}
      {hasMorePipelines && (
        <div className="flex justify-center">
          <button
            onClick={() => setShowAllPipelines(!showAllPipelines)}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
          >
            {showAllPipelines
              ? `Show Less (${maxVisiblePipelines} pipelines)`
              : `Show All Pipelines (${pipelines.length - maxVisiblePipelines} more)`
            }
          </button>
        </div>
      )}
    </div>
  );
}